// File: frontend/src/components/ums/UserTable.js

import React from 'react';
import { Eye, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../utility/LoadingSpinner';
import EmptyState from '../utility/EmptyState';
import styles from './UserTable.module.css';

const UserTable = ({ 
  users, 
  totalCount, 
  isLoading, 
  hasMore, 
  onLoadMore, 
  onViewDetails 
}) => {
  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatUserName = (user) => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.is_anonymous ? 'Anonymous User' : 'No Name';
  };

  const getBadgeClass = (value, type) => {
    if (type === 'status') {
      switch (value) {
        case 'active': return styles.badgeSuccess;
        case 'inactive': return styles.badgeWarning;
        case 'suspended': return styles.badgeError;
        default: return styles.badgeDefault;
      }
    }
    if (type === 'role') {
      if (value === 'super_admin' || value === 'admin') return styles.badgeError;
      if (value === 'manager' || value === 'advisor') return styles.badgeInfo;
      return styles.badgeDefault;
    }
    if (type === 'anonymous') {
      return value ? styles.badgeWarning : styles.badgeSuccess;
    }
    return styles.badgeDefault;
  };

  const formatRoleLabel = (role) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (isLoading && users.length === 0) {
    return (
      <div className={styles.tableCard}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="large" text="Loading users..." />
        </div>
      </div>
    );
  }

  if (!isLoading && users.length === 0) {
    return (
      <div className={styles.tableCard}>
        <EmptyState
          title="No users found"
          message="Try adjusting your filters or search criteria"
        />
      </div>
    );
  }

  return (
    <div className={styles.tableCard}>
      {/* Header */}
      <div className={styles.tableHeader}>
        <h2 className={styles.tableTitle}>
          Users 
          {totalCount !== null && (
            <span className={styles.userCount}>
              ({users.length} of {totalCount})
            </span>
          )}
          {totalCount === null && (
            <span className={styles.userCount}>
              ({users.length})
            </span>
          )}
        </h2>
      </div>

      {/* Mobile Card View */}
      <div className={styles.mobileView}>
        {users.map((user) => (
          <div key={user.user_id} className={styles.userCard}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderLeft}>
                <div className={styles.userName}>{formatUserName(user)}</div>
                <div className={styles.userEmail}>{user.email_address}</div>
              </div>
              <button
                onClick={() => onViewDetails(user)}
                className={styles.viewButtonMobile}
                aria-label="View user details"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className={styles.cardBody}>
              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Status:</span>
                <span className={`${styles.badge} ${getBadgeClass(user.user_status, 'status')}`}>
                  {user.user_status}
                </span>
              </div>

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Role:</span>
                <span className={`${styles.badge} ${getBadgeClass(user.user_role, 'role')}`}>
                  {formatRoleLabel(user.user_role)}
                </span>
              </div>

              {user.phone_number && (
                <div className={styles.cardRow}>
                  <span className={styles.cardLabel}>Phone:</span>
                  <span className={styles.cardValue}>
                    {user.phone_number}
                    {user.is_phone_verified && (
                      <span className={styles.verifiedBadge} title="Verified">✓</span>
                    )}
                  </span>
                </div>
              )}

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Journey:</span>
                <span className={styles.cardValue}>{user.journey_stage}</span>
              </div>

              <div className={styles.cardRow}>
                <span className={styles.cardLabel}>Created:</span>
                <span className={styles.cardValue}>{formatDate(user.created_at)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className={styles.desktopView}>
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Role</th>
                <th>Journey Stage</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.user_id}>
                  <td className={styles.nameCell}>
                    <div className={styles.userName}>{formatUserName(user)}</div>
                    {user.is_anonymous && (
                      <span className={`${styles.badge} ${styles.badgeSmall} ${styles.badgeWarning}`}>
                        Anonymous
                      </span>
                    )}
                  </td>

                  <td className={styles.emailCell}>
                    {user.email_address}
                    {user.is_email_verified && (
                      <span className={styles.verifiedBadge} title="Verified">✓</span>
                    )}
                  </td>

                  <td className={styles.phoneCell}>
                    {user.phone_number || '-'}
                    {user.phone_number && user.is_phone_verified && (
                      <span className={styles.verifiedBadge} title="Verified">✓</span>
                    )}
                  </td>

                  <td>
                    <span className={`${styles.badge} ${getBadgeClass(user.user_status, 'status')}`}>
                      {user.user_status}
                    </span>
                  </td>

                  <td>
                    <span className={`${styles.badge} ${getBadgeClass(user.user_role, 'role')}`}>
                      {formatRoleLabel(user.user_role)}
                    </span>
                  </td>

                  <td className={styles.journeyCell}>
                    {user.journey_stage}
                  </td>

                  <td className={styles.dateCell}>
                    {formatDate(user.created_at)}
                  </td>

                  <td>
                    <button
                      onClick={() => onViewDetails(user)}
                      className={styles.viewButton}
                      aria-label="View user details"
                    >
                      <Eye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More */}
      {hasMore && (
        <div className={styles.loadMoreContainer}>
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className={styles.loadMoreButton}
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="small" />
                Loading...
              </>
            ) : (
              'Load More Users'
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserTable;